import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Store, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SEO, breadcrumbJsonLd } from '@/components/SEO';
import { neutralProviderName } from '@/lib/providerName';
import { absoluteUrl, currentUrlLang, pathForLang } from '@/i18n/urlLanguage';

/**
 * SEO page « transfert aéroport Phuket » (/transfert-aeroport-phuket,
 * /en/phuket-airport-transfer): how to book, the real public airport
 * transfers (GET /api/seo/catalog/airport, the same list as the HTML served
 * before JavaScript by functions/_lib/catalogPage.js), practical information
 * and FAQ. Always indexable, like /transport (its own text; the offers are
 * only added to it). The « Sans acompte » badge and note only appear for
 * offers whose deposit really is 0 THB (`no_deposit` from the API).
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FR_PATH = '/transfert-aeroport-phuket';

export const AirportTransferPhuket = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = currentUrlLang() || 'fr';
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  // One slug per language: the other language's slug opens the right page.
  useEffect(() => {
    const expected = pathForLang(lang, FR_PATH);
    const current = `${lang === 'en' ? '/en' : ''}${location.pathname === '/' ? '' : location.pathname}`;
    if (current.replace(/\/+$/, '') !== expected) window.location.replace(expected);
  }, [lang, location.pathname]);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/seo/catalog/airport`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  const k = (key, opts) => t(`localPages.airportTransfer.${key}`, opts);
  const list = (key) => { const v = k(key, { returnObjects: true }); return Array.isArray(v) ? v : []; };
  const listings = data?.listings || [];
  const anyNoDeposit = listings.some((r) => r.no_deposit === true);
  const number = (n) => Number(n).toLocaleString(i18n.language === 'en' ? 'en-US' : 'fr-FR');
  const priceText = (r) => {
    if (!(Number(r.price) > 0)) return null;
    const from = Number(r.price_high) > Number(r.price) ? `${t('seo.catalog.from')} ` : '';
    return `${from}${number(r.price)} ${r.currency || 'THB'} ${t('seo.catalog.units.trajet')}`;
  };

  const jsonLd = [
    breadcrumbJsonLd([
      { name: t('seo.breadcrumb.home'), path: '/' },
      { name: t('seo.breadcrumb.transport'), path: '/transport' },
      { name: k('breadcrumb'), path: FR_PATH },
    ]),
    ...(listings.length ? [{
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: t('seo.catalog.airport.offersTitle'),
      numberOfItems: listings.length,
      itemListElement: listings.map((r, i) => ({
        '@type': 'ListItem', position: i + 1, name: r.title, url: absoluteUrl(lang, r.path),
      })),
    }] : []),
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: list('faq').map((f) => ({
        '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" data-testid="airport-transfer-page">
      <SEO
        title={k('seoTitle')}
        description={k('seoDescription')}
        canonical={absoluteUrl(lang, FR_PATH)}
        jsonLd={jsonLd}
      />
      <Header />

      <main className="pt-24 pb-16">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-ink-light mb-4 flex flex-wrap items-center gap-1" aria-label="breadcrumb">
            <Link to="/" className="hover:text-accent">{t('seo.breadcrumb.home')}</Link>
            <span aria-hidden="true">›</span>
            <Link to="/transport" className="hover:text-accent">{t('seo.breadcrumb.transport')}</Link>
            <span aria-hidden="true">›</span>
            <span className="text-primary font-medium">{k('breadcrumb')}</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary mb-5 break-words" data-testid="airport-h1">
            {k('h1')}
          </h1>
          <div className="max-w-3xl space-y-3 text-base sm:text-lg text-ink-light leading-relaxed">
            <p>{k('intro1')}</p>
            <p>{k('intro2')}</p>
          </div>
          <a href="#offers" className="btn-primary mt-6 inline-flex items-center gap-2" data-testid="airport-cta">
            {k('cta')} <ArrowRight className="w-4 h-4" />
          </a>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          <h2 className="text-2xl font-semibold text-primary mb-5">{k('howTitle')}</h2>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="airport-steps">
            {list('steps').map((step, i) => (
              <li key={step} className="rounded-2xl bg-background-soft p-5 flex gap-3">
                <span className="w-7 h-7 shrink-0 rounded-full bg-accent text-white text-sm font-bold flex items-center justify-center">{i + 1}</span>
                <p className="text-sm text-ink-light leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 scroll-mt-24" id="offers">
          <h2 className="text-2xl font-semibold text-primary mb-5">{t('seo.catalog.airport.offersTitle')}</h2>
          {anyNoDeposit && (
            <p className="mb-6 flex items-start gap-2 rounded-2xl bg-emerald-50 text-emerald-900 text-sm px-4 py-3 max-w-3xl" data-testid="airport-no-deposit-note">
              <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
              <span>{k('noDepositNote')}</span>
            </p>
          )}
          {!data && !failed && <p className="text-ink-light" data-testid="airport-loading">{k('loading')}</p>}
          {(failed || (data && listings.length === 0)) && (
            <div className="rounded-2xl border border-slate-200 p-6 text-center" data-testid="airport-empty">
              <p className="text-ink-light mb-4">{k('empty')}</p>
              <Link to="/transport" className="btn-primary inline-flex items-center gap-2">{k('seeAll')} <ArrowRight className="w-4 h-4" /></Link>
            </div>
          )}
          {listings.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="airport-offers">
              {listings.map((r) => (
                <li key={r.service_id} className="card-offer overflow-hidden flex flex-col" data-testid={`airport-offer-${r.service_id}`}>
                  <Link to={r.path} className="block relative h-52 bg-slate-100 overflow-hidden" tabIndex={-1} aria-hidden="true">
                    {r.image && (
                      <img src={r.image} alt="" width="640" height="416" loading="lazy" className="w-full h-full object-cover" />
                    )}
                    {r.no_deposit === true && (
                      <span className="absolute top-3 right-3 bg-emerald-500 text-white rounded-full px-3 py-1 text-xs font-semibold shadow-sm" data-testid={`no-deposit-badge-${r.service_id}`}>
                        {t('seo.catalog.noDeposit')}
                      </span>
                    )}
                  </Link>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-lg font-semibold text-primary leading-tight break-words">{r.title}</h3>
                    <p className="mt-1 text-sm text-ink-light flex items-center gap-1 min-w-0">
                      <Store className="w-4 h-4 text-accent shrink-0" />
                      <span className="truncate">{t('seo.catalog.provider', { name: r.provider_name || neutralProviderName() })}</span>
                    </p>
                    <div className="mt-auto pt-4 flex flex-wrap items-end justify-between gap-3 border-t border-slate-100">
                      {priceText(r) && (
                        <p className="text-lg font-bold text-primary" data-testid={`airport-price-${r.service_id}`}>{priceText(r)}</p>
                      )}
                      <Link to={r.path} className="btn-primary text-sm inline-flex items-center gap-1" data-testid={`airport-view-${r.service_id}`}>
                        {t('seo.catalog.viewListing')} <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {list('sections').map((s) => (
            <div key={s.title} className="rounded-2xl bg-background-soft p-6">
              <h2 className="text-lg font-semibold text-primary mb-2">{s.title}</h2>
              <p className="text-sm text-ink-light leading-relaxed">{s.text}</p>
            </div>
          ))}
        </section>

        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-14" data-testid="airport-faq">
          <h2 className="text-2xl font-semibold text-primary mb-5">{k('faqTitle')}</h2>
          <div className="divide-y divide-slate-200 border-y border-slate-200">
            {list('faq').map((f) => (
              <details key={f.q} className="py-4 group">
                <summary className="cursor-pointer font-medium text-primary list-none flex justify-between gap-4">
                  <span>{f.q}</span><span className="text-accent group-open:rotate-45 transition-transform" aria-hidden="true">+</span>
                </summary>
                <p className="mt-3 text-sm text-ink-light leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/transport" className="btn-primary inline-flex items-center gap-2" data-testid="airport-see-all">{k('seeAll')} <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AirportTransferPhuket;
